import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import cookies from 'js-cookie';
import { useSelector } from 'react-redux';
import { encode, decode } from 'js-base64';
import Clipboard from 'react-clipboard.js';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/styles';
import { Empty, message, Spin, Divider, Input, Button, Tooltip, Alert } from 'antd';
import {
  InfoCircleOutlined,
  ClockCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';

import { cookieNames } from '~client/constants/storage';
import { apiTokenStatus } from '~constants/login-security';
import {
  QUERY_GET_API_TOKENS,
  MUTATION_CREATE_API_TOKEN,
  QUERY_LANDLORD_WEBHOOK,
  MUTATION_CREATE_LANDLORD_SUBSCRIPTION,
  MUTATION_UPDATE_LANDLORD_SUBSCRIPTION,
} from './queries';

import { IconCircleQuestion, IconAlertWarning } from 'common/components/svgs';
import { getSafeHtml } from 'common/helpers/safeHtml';

const uesStyles = makeStyles(
  theme => ({
    loading: {
      ...theme.flex.center,
      height: '100%',
    },

    apiTokenContainer: {
      paddingLeft: 32,
      width: '100%',

      '& .ant-tooltip-inner a': {
        color: theme.colors.mainGreen6,

        '&:hover': {
          color: theme.colors.mainGreen6,
        },
      },
    },

    title: {
      ...theme.typography.h3,
      fontWeight: 600,
      paddingBottom: 16,
      borderBottom: `1px solid ${theme.colors.gray4}`,
      color: theme.colors.titleColor,
      marginBottom: 32,
    },

    emptyContainer: {
      ...theme.flex.center,
      width: 520,
      height: 320,
      marginTop: 16,
      borderRadius: 2,
      padding: '0 60px',
      backgroundColor: theme.colors.gray2,

      '& .ant-empty-description': {
        color: theme.colors.disableColor,
      },

      '& .ant-empty': {
        ...theme.flex.center,
        ...theme.flex.col,
      },
    },

    applyButton: {
      ...theme.typography.h5,
      color: theme.colors.bodyColor,
      fontWeight: 500,
      padding: '5px 16px',
      border: `1px solid ${theme.colors.gray5}`,
      backgroundColor: theme.colors.gray1,
      borderRadius: 2,
      width: 'fit-content',
      marginTop: 8,
      cursor: 'pointer',
    },

    keyIcon: {
      width: 14,
      height: 14,
      marginRight: 8,
    },

    apiTokenCard: {
      paddingRight: 24,
    },

    cardTitle: {
      ...theme.typography.h5,
      color: theme.colors.titleColor,
    },

    card: {
      marginTop: 16,
      borderRadius: 2,
      padding: '20px 24px',
      backgroundColor: theme.colors.gray2,
      maxWidth: 520,
    },

    icon: {
      '& svg': {
        width: 12,
        height: 12,
      },
      marginRight: 6,
    },

    cardContent: {
      ...theme.typography.body2,
      color: theme.colors.captionColor,
      marginBottom: 12,
    },

    cardTokenContent: ({ apiToken }) => ({
      marginTop: apiToken?.description ? 24 : 0,
      marginBottom: 20,
    }),

    tokenValue: {
      ...theme.flex.crossCenter,
      ...theme.typography.h4,
      color: theme.colors.titleColor,
    },

    tokenNoValue: {
      ...theme.flex.crossCenter,
      ...theme.typography.h4,
      letterSpacing: 2,
    },

    eyeIcon: {
      width: 14,
      height: 14,
      color: theme.colors.mainGreen6,
      marginLeft: 8,
    },

    eyeInvisibleIcon: {
      display: 'inline-block',
      width: 14,
      height: 14,
      color: theme.colors.mainGreen6,
      marginLeft: 8,
      marginTop: -5,
    },

    copyContent: {
      display: 'flex',
      marginTop: 8,
    },

    copyLink: {
      fontSize: 12,
      lineHeight: '20px',
      color: theme.colors.mainGreen6,
      cursor: 'pointer',
      border: 'none',
      padding: 0,
    },

    commonContent: {
      fontSize: 12,
      lineHeight: '20px',
      color: theme.colors.bodyColor,
    },

    cardBottom: {
      ...theme.typography.body4,
      color: theme.colors.captionColor,
      marginTop: 20,
      paddingTop: 8,
      borderTop: `1px solid ${theme.colors.gray4}`,
    },

    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 16,

      '& .section-title--text': {
        ...theme.typography.body3,
        color: theme.colors.titleColor,
        paddingRight: 6,
      },
    },

    webHookDivider: {
      width: 520,
      minWidth: 520,
    },

    warningText: {
      width: 520,
      padding: '6px 12px 6px 32px',
      border: 'none',
      marginBottom: 12,

      '& .ant-alert-icon': {
        width: 14,
        height: 14,
        top: 9,
        left: 12,
      },

      '& .ant-alert-message': {
        display: 'none',
      },

      '& .ant-alert-description': {
        ...theme.typography.body5,
        color: theme.colors.orange6,
      },
    },

    webhook: {
      position: 'relative',
      width: 520,

      '& .webhook-content': {
        ...theme.typography.body2,
        color: theme.colors.bodyColor,
        marginBottom: 0,
      },

      '& .webhook-error': {
        color: theme.colors.red11,
        marginTop: 4,
        marginBottom: 0,

        '&-text': {
          ...theme.typography.body5,
          paddingLeft: 6,
        },
      },

      '& .webhook-input': {
        marginTop: 8,
        marginBottom: 16,

        '&-error': {
          ...theme.typography.body2,
          color: theme.colors.red11,
          marginBottom: 16,
        },

        '&--error': {
          borderColor: theme.colors.red11,
          boxShadow: 'none',
          marginBottom: 0,
        },
      },

      '&>.ant-btn': {
        position: 'absolute',
        right: 0,
        top: 0,
        height: 22,
        fontWeight: 600,
        paddingRight: 0,
      },

      '& .webhook-confirm-btn': {
        fontWeight: 600,
      },
    },
  }),
  { name: 'APIToken' },
);

const URL_REGEXP =
  /(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?\/[a-zA-Z0-9]{2,}|((https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?)|(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}(\.[a-zA-Z0-9]{2,})?/;

const APIToken = ({ t }) => {
  const { landlordId } = useSelector(state => ({
    landlordId: state.pmsReducer.dashboard.account.toJS()?.account?.landlordId,
  }));

  const classes = uesStyles({ apiToken });

  const [applyStatus, setApplyStatus] = useState();
  const [visible, setVisible] = useState(false);
  const [apiToken, setApiToken] = useState();

  const [webhookEditing, setWebhookEditing] = useState(false);
  const [webhookVal, setWebhookVal] = useState(null);
  const [, setWebhookValValid] = useState(false);
  const [webhookValError, setWebhookValError] = useState(false);

  const decodedValue = landlordId ? decode(landlordId) : '';
  const roleId = encode(decodedValue?.replace('Landlord', 'RoleId'));

  const slug = cookies.get(cookieNames.landlordSlug);

  const [queryAPIToken, { loading }] = useLazyQuery(QUERY_GET_API_TOKENS, {
    onCompleted: data => {
      if (data?.getApiTokens) {
        const apiNode = data.getApiTokens.edges?.[0]?.node;
        setApiToken(apiNode);
        setApplyStatus(apiNode?.status);
      }
    },
  });

  const [createApiToken, { loading: applyLoading }] = useMutation(MUTATION_CREATE_API_TOKEN, {
    variables: {
      input: {
        roleId,
      },
    },
    onCompleted: result => {
      if (result.createApiToken?.id) {
        setApplyStatus(apiTokenStatus.PENDING);
        message.success('You have successfully applied!', 3);
      }
    },
  });

  const { data, refetch } = useQuery(QUERY_LANDLORD_WEBHOOK, {
    variables: { slug },
    onCompleted: data => {
      if (data?.landlord) {
        setWebhookVal(data?.landlord?.webhook?.callbackUrl);
        setWebhookValValid(data?.landlord?.webhook?.valid);
      }
    },
  });

  const [createWebhookUrl, { loading: createLoading }] = useMutation(
    MUTATION_CREATE_LANDLORD_SUBSCRIPTION,
    {
      onCompleted: result => {
        if (result?.createLandlordSubscription?.callbackUrl) {
          handleToggleEditing();
          setWebhookVal(result?.createLandlordSubscription?.callbackUrl);
          setWebhookValValid(true);

          refetch();
        }
      },
      onError: e => {
        if (e?.message === 'NOT_EXIST_API_TOKEN') {
          message.error(t('landlord.account.login_security.webhook.error_token_message'));
        } else if (e) {
          message.error(e.toString());
        } else {
          message.error(t('Something wrong, please try again.'));
        }
      },
    },
  );

  const [updateWebhookUrl, { loading: updateLoading }] = useMutation(
    MUTATION_UPDATE_LANDLORD_SUBSCRIPTION,
    {
      onCompleted: result => {
        if (result?.updateLandlordSubscription?.callbackUrl) {
          handleToggleEditing();
          setWebhookVal(result?.updateLandlordSubscription?.callbackUrl);
          setWebhookValValid(true);

          refetch();
        }
      },
      onError: e => {
        if (e?.message === 'NOT_EXIST_API_TOKEN') {
          message.error(t('landlord.account.login_security.webhook.error_token_message'));
        } else if (e) {
          message.error(e.toString());
        } else {
          message.error(t('Something wrong, please try again.'));
        }
      },
    },
  );

  const handleToggleEditing = () => {
    if (webhookEditing) {
      setWebhookVal(data?.landlord?.webhook?.callbackUrl);
      setWebhookValValid(data?.landlord?.webhook?.valid);
    }

    setWebhookEditing(prevVal => !prevVal);
    setWebhookValError(null);
  };

  const handleEyeClick = () => {
    setVisible(!visible);
  };

  const landlord = data?.landlord ?? {};

  const handleWebhookChange = value => {
    const val = value?.trim();

    setWebhookVal(val);

    if (!URL_REGEXP.test(val)) {
      setWebhookValError(t('landlord.account.login_security.webhook.error_message'));
    } else {
      setWebhookValError(null);
    }
  };

  const handleConfirmWebhook = () => {
    const params = {
      variables: {
        input: {
          callbackUrl: webhookVal,
          name: landlord.name,
          landlordId: landlord.id,
        },
      },
    };

    if (landlord?.webhook !== null) {
      updateWebhookUrl(params);
    } else {
      createWebhookUrl(params);
    }
  };

  useEffect(() => {
    if (roleId) {
      queryAPIToken({
        variables: {
          roleId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  return (
    <div className={classes.apiTokenContainer} id="apiToken">
      <If condition={loading || applyLoading}>
        <div className={classes.loading}>
          <Spin />
        </div>
      </If>
      <If condition={!loading && !applyLoading}>
        <div className={classes.title}>{t('landlord.account.login_security.api_token.title')}</div>
        <section>
          <p className={classes.sectionTitle}>
            <span className="section-title--text">
              {t('landlord.account.login_security.api_token.subTitle')}
            </span>

            <Tooltip
              title={
                <span>{getSafeHtml(t('landlord.account.login_security.api_token.tooltip'))}</span>
              }
              placement="topLeft"
              arrowPointAtCenter
              getPopupContainer={() => document.querySelector('#apiToken')}
            >
              <IconCircleQuestion />
            </Tooltip>
          </p>
          <Choose>
            <When condition={!apiToken?.apiToken && applyStatus !== apiTokenStatus.PENDING}>
              <div className={classes.emptyContainer}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('dashboard.pms_dashboard.account.login_security.token.empty_desc')}
                >
                  <div className={classes.applyButton} onClick={createApiToken}>
                    <span>
                      {t('dashboard.pms_dashboard.account.login_security.token.apple_token')}
                    </span>
                  </div>
                </Empty>
              </div>
            </When>
            <When condition={applyStatus === apiTokenStatus.PENDING}>
              <div className={classes.apiTokenCard}>
                <div className={classes.card}>
                  <div className={classes.cardContent}>
                    <ClockCircleOutlined className={classes.icon} />
                    <span>
                      {t('dashboard.pms_dashboard.account.login_security.token.waiting_approval')}
                    </span>
                  </div>
                  <div className={classes.cardBottom}>
                    {t('dashboard.pms_dashboard.account.login_security.token.apply_submit')}
                  </div>
                </div>
              </div>
            </When>
            <Otherwise>
              <div className={classes.apiTokenCard}>
                {/* <p className={classes.cardTitle}>{apiToken?.label}</p> */}
                <div className={classes.card}>
                  <If condition={apiToken?.description}>
                    <div className={classes.cardContent}>
                      <InfoCircleOutlined className={classes.icon} />
                      <span>{apiToken?.description}</span>
                    </div>
                  </If>
                  <div className={classes.cardTokenContent}>
                    <Choose>
                      <When condition={visible}>
                        <div className={classes.tokenValue}>
                          <span>{apiToken?.apiToken}</span>
                          <EyeInvisibleOutlined
                            onClick={handleEyeClick}
                            className={classes.eyeIcon}
                          />
                        </div>
                        <div className={classes.copyContent}>
                          <Clipboard
                            className={classes.copyLink}
                            data-clipboard-text={apiToken?.apiToken}
                            onSuccess={() => {
                              message.success('copy successfully');
                            }}
                          >
                            {t('dashboard.pms_dashboard.account.login_security.token.copy_prefix')}
                          </Clipboard>
                          <span className={classes.commonContent}>
                            {t(
                              'dashboard.pms_dashboard.account.login_security.token.connect_student_com',
                            )}
                          </span>
                        </div>
                      </When>
                      <Otherwise>
                        <div className={classes.tokenNoValue}>
                          <span>******************************</span>
                          <EyeOutlined
                            onClick={handleEyeClick}
                            className={classes.eyeInvisibleIcon}
                          />
                        </div>
                      </Otherwise>
                    </Choose>
                  </div>
                  <div className={classes.cardBottom}>
                    {apiToken?.lastAccessedAt
                      ? t('dashboard.pms_dashboard.account.login_security.token.last_time', {
                          time: moment(apiToken?.lastAccessedAt).format('YYYY-MM-DD HH:mm'),
                        })
                      : t('dashboard.pms_dashboard.account.login_security.token.never_used_so_for')}
                  </div>
                </div>
              </div>
            </Otherwise>
          </Choose>
        </section>

        <Divider className={classes.webHookDivider} />

        <If condition={apiToken?.apiToken && !data?.landlord?.webhook?.callbackUrl}>
          <Alert
            type="warning"
            showIcon
            icon={<IconAlertWarning />}
            className={classes.warningText}
            description={t('landlord.account.login_security.webhook.warning_message')}
          />
        </If>

        <section className={classes.webhook}>
          <div>
            <p className={classes.sectionTitle} style={{ marginBottom: 6 }}>
              <span className="section-title--text">
                {t('landlord.account.login_security.webhook.title')}
              </span>

              <Tooltip
                title={t('landlord.account.login_security.webhook.tooltip')}
                placement="topLeft"
                arrowPointAtCenter
              >
                <IconCircleQuestion />
              </Tooltip>
            </p>
            <p className="webhook-content">
              {t('landlord.account.login_security.webhook.url')}:
              <Choose>
                <When condition={webhookEditing}>
                  <Input
                    className={classNames('webhook-input', {
                      'webhook-input--error': webhookValError,
                    })}
                    value={webhookVal}
                    onChange={ev => handleWebhookChange(ev.target.value)}
                  />

                  <If condition={webhookValError}>
                    <p className="webhook-input-error">{webhookValError}</p>
                  </If>

                  <Button
                    type="primary"
                    className="webhook-confirm-btn"
                    loading={createLoading || updateLoading}
                    disabled={webhookValError}
                    onClick={handleConfirmWebhook}
                  >
                    {t('landlord.account.login_security.webhook.confirm_btn')}
                  </Button>
                </When>
                <Otherwise>
                  <span>{webhookVal || '-'}</span>

                  <If
                    condition={
                      !webhookEditing && data?.landlord?.webhook && !data?.landlord?.webhook?.valid
                    }
                  >
                    <p className="webhook-error">
                      <ExclamationCircleFilled style={{ fontSize: 12 }} />
                      <span className="webhook-error-text">
                        {t('landlord.account.login_security.webhook.error')}
                      </span>
                    </p>
                  </If>

                  <Divider className={classes.webHookDivider} />
                </Otherwise>
              </Choose>
            </p>
          </div>

          <Button type="link" className="webhook-btn" onClick={handleToggleEditing}>
            {t(
              `landlord.account.login_security.webhook.${
                webhookEditing ? 'cancel' : landlord?.webhook ? 'edit' : 'add'
              }_btn`,
            )}
          </Button>
        </section>
      </If>
    </div>
  );
};

export default APIToken;
